import { CopilotConversationContentBlock } from './copilotConversation';

//
// The chart a Chart content block carries (present kind Chart of the general knowledge loop): the payload is the
// definition the server validated (ChartDefinition.cs, ported from SkillControl's chart_render, inline points only).
// The types follow SkillControl's chartDefinition.ts; nothing here depends on it.
//
export type ChartKind = 'bar' | 'stackedBar' | 'line' | 'area' | 'donut';

const chartKinds: ChartKind[] = ['bar', 'stackedBar', 'line', 'area', 'donut'];

export interface ChartPoint {
  label: string;
  // null: no value for this label (a gap, never drawn as 0)
  value: number | null;
}

export interface ChartSeries {
  name: string;
  points: ChartPoint[];
}

export interface ChartOptions {
  xTitle?: string;
  yTitle?: string;
  valueFormat?: 'number' | 'percent';
}

export interface ChartDefinition {
  version: number;
  kind: ChartKind;
  title: string;
  series: ChartSeries[];
  options?: ChartOptions;
}

// the definition of a Chart block, or undefined when the payload is none (another version, a damaged payload)
export const parseChartBlockPayload = (block: CopilotConversationContentBlock): ChartDefinition | undefined => {
  const raw = block.payload as
    | { version?: unknown; kind?: unknown; title?: unknown; series?: unknown; options?: Record<string, unknown> }
    | undefined;
  if (!raw || typeof raw !== 'object') return undefined;
  if (raw.version !== 1 || !chartKinds.includes(raw.kind as ChartKind) || !Array.isArray(raw.series)) return undefined;

  const series: ChartSeries[] = raw.series
    .filter((item: unknown) => !!item && typeof item === 'object')
    .map((item: { name?: unknown; points?: unknown }) => ({
      name: typeof item.name === 'string' ? item.name : '',
      points: (Array.isArray(item.points) ? item.points : [])
        .filter((point: unknown) => !!point && typeof point === 'object')
        .map((point: { label?: unknown; value?: unknown }) => ({
          label: typeof point.label === 'string' ? point.label : '',
          value: typeof point.value === 'number' && Number.isFinite(point.value) ? point.value : null,
        })),
    }));

  return {
    version: 1,
    kind: raw.kind as ChartKind,
    title: typeof raw.title === 'string' ? raw.title : block.title ?? '',
    series,
    options: {
      xTitle: typeof raw.options?.xTitle === 'string' ? raw.options.xTitle : undefined,
      yTitle: typeof raw.options?.yTitle === 'string' ? raw.options.yTitle : undefined,
      valueFormat: raw.options?.valueFormat === 'percent' ? 'percent' : 'number',
    },
  };
};

// a definition with nothing to draw: no series, or no value in any series
export const hasChartValues = (definition: ChartDefinition | undefined): definition is ChartDefinition =>
  !!definition && definition.series.some((series) => series.points.some((point) => point.value !== null));
